import { useState, useEffect } from 'react';
import axios from 'axios';
import { Phone, Envelope, CalendarBlank, ClockCounterClockwise, ArrowLeft, CheckCircle, PencilSimple, Trash, Check, X } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

export const PendingPaymentsDetailView = ({ onBack }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingPayment, setEditingPayment] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [stats, setStats] = useState({ total: 0, count: 0, customers: 0 });

  const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(amount || 0);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/pending-payments/detailed`);
      setPayments(response.data);
      const total = response.data.reduce((sum, p) => sum + p.amount, 0);
      const customers = new Set(response.data.map(p => p.customer_name).filter(Boolean)).size;
      setStats({ total, count: response.data.length, customers });
    } catch (error) { toast.error('Failed to load pending payments'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPayments(); }, []);

  const handleMarkPaid = async (payment) => {
    try {
      await axios.patch(`${API}/pending-payments/mark-paid`, null, {
        params: { date: payment.date, invoice_number: payment.invoice_number || payment.description }
      });
      toast.success('Payment marked as paid!');
      fetchPayments();
    } catch (error) { toast.error('Failed to mark as paid'); }
  };

  const handleEdit = (payment) => {
    setEditingPayment(payment);
    setEditForm({ ...payment });
  };

  const handleUpdate = async () => {
    try {
      if (editingPayment.payment_id) {
        await axios.put(`${API}/pending-payments/${editingPayment.payment_id}`, editForm);
      } else {
        await axios.put(`${API}/pending-payments`, editForm, { params: { date: editingPayment.date, invoice_number: editingPayment.invoice_number } });
      }
      toast.success('Payment updated!');
      setEditingPayment(null);
      fetchPayments();
    } catch (error) { toast.error('Failed to update payment'); }
  };

  const handleDelete = async (payment) => {
    if (!window.confirm(`Delete payment ${payment.invoice_number || payment.payment_id}?`)) return;
    try {
      if (payment.payment_id) {
        await axios.delete(`${API}/pending-payments/${payment.payment_id}`);
      } else {
        await axios.delete(`${API}/pending-payments`, { params: { date: payment.date, invoice_number: payment.invoice_number } });
      }
      toast.success('Payment deleted!');
      fetchPayments();
    } catch (error) { toast.error('Failed to delete payment'); }
  };

  const filteredPayments = payments.filter(p =>
    (p.customer_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (p.invoice_number || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (p.remarks || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getInitials = (name) => name ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '?';

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5f7fa] to-[#e8ecf1] p-6" data-testid="pending-detail-view">
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={onBack} className="premium-button" data-testid="back-button"><ArrowLeft size={20} className="mr-2" /> Back</Button>
          <h1 className="heading text-4xl font-bold gradient-warning">Pending Payments</h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="premium-card stat-card p-6">
            <p className="text-sm text-gray-600 mb-1">Total Pending</p>
            <h3 className="currency text-3xl font-bold gradient-warning">{formatCurrency(stats.total)}</h3>
          </Card>
          <Card className="premium-card stat-card p-6">
            <p className="text-sm text-gray-600 mb-1">Pending Count</p>
            <h3 className="text-3xl font-bold gradient-text">{stats.count}</h3>
          </Card>
          <Card className="premium-card stat-card p-6">
            <p className="text-sm text-gray-600 mb-1">Unique Customers</p>
            <h3 className="text-3xl font-bold gradient-text">{stats.customers}</h3>
          </Card>
        </div>

        <Card className="premium-card p-4 mb-6">
          <Input placeholder="Search by customer name or invoice..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="border-0 focus:ring-2 focus:ring-purple-500" data-testid="pending-search" />
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPayments.length === 0 ? (
            <Card className="premium-card p-12 col-span-full text-center"><p className="text-gray-500">No pending payments found</p></Card>
          ) : filteredPayments.map((payment, i) => (
            <Card key={payment.payment_id || i} className="premium-card p-6 hover:shadow-lg transition-shadow" data-testid={`payment-card-${i}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="customer-avatar">{getInitials(payment.customer_name)}</div>
                  <div>
                    <h3 className="font-bold text-gray-900">{payment.customer_name || 'Unknown'}</h3>
                    <p className="text-sm text-gray-500">{payment.invoice_number || '-'}</p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${payment.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                  {payment.status === 'paid' ? 'Paid' : 'Pending'}
                </span>
              </div>

              <div className="space-y-1.5 mb-4 text-sm text-gray-600">
                {payment.customer_phone && <div className="flex items-center gap-2"><Phone size={14} /><span>{payment.customer_phone}</span></div>}
                {payment.customer_email && <div className="flex items-center gap-2"><Envelope size={14} /><span className="truncate">{payment.customer_email}</span></div>}
                {payment.due_date && <div className="flex items-center gap-2 text-orange-600"><CalendarBlank size={14} /><span>Due: {payment.due_date}</span></div>}
                <div className="flex items-center gap-2"><CalendarBlank size={14} /><span>Added: {payment.date}</span></div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t mb-3">
                <span className="currency text-2xl font-bold text-orange-600">{formatCurrency(payment.amount)}</span>
              </div>

              <div className="flex gap-2">
                {payment.status !== 'paid' && (
                  <Button size="sm" onClick={() => handleMarkPaid(payment)} className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs" data-testid={`mark-paid-${i}`}>
                    <CheckCircle size={14} className="mr-1" /> Paid
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => handleEdit(payment)} className="text-xs" data-testid={`edit-payment-${i}`}>
                  <PencilSimple size={14} className="mr-1" /> Edit
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleDelete(payment)} className="text-xs text-red-600 hover:bg-red-50" data-testid={`delete-payment-${i}`}>
                  <Trash size={14} />
                </Button>
              </div>

              {payment.remarks && <p className="text-xs text-gray-500 mt-3 pt-3 border-t">Note: {payment.remarks}</p>}
            </Card>
          ))}
        </div>
      </div>

      <Dialog open={editingPayment !== null} onOpenChange={() => setEditingPayment(null)}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader><DialogTitle className="heading text-xl font-bold">Edit Payment</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-semibold">Invoice Number</label><Input value={editForm.invoice_number || ''} onChange={(e) => setEditForm({ ...editForm, invoice_number: e.target.value })} /></div>
              <div><label className="text-sm font-semibold">Amount</label><Input type="number" step="0.01" value={editForm.amount || ''} onChange={(e) => setEditForm({ ...editForm, amount: parseFloat(e.target.value) || 0 })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-semibold">Customer Name</label><Input value={editForm.customer_name || ''} onChange={(e) => setEditForm({ ...editForm, customer_name: e.target.value })} /></div>
              <div><label className="text-sm font-semibold">Phone</label><Input value={editForm.customer_phone || ''} onChange={(e) => setEditForm({ ...editForm, customer_phone: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-semibold">Email</label><Input type="email" value={editForm.customer_email || ''} onChange={(e) => setEditForm({ ...editForm, customer_email: e.target.value })} /></div>
              <div><label className="text-sm font-semibold">Due Date</label><Input type="date" value={editForm.due_date || ''} onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })} /></div>
            </div>
            <div><label className="text-sm font-semibold">Remarks</label><Input value={editForm.remarks || ''} onChange={(e) => setEditForm({ ...editForm, remarks: e.target.value })} /></div>
            <div className="flex gap-2 pt-2">
              <Button onClick={handleUpdate} className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white"><Check size={16} className="mr-2" /> Save</Button>
              <Button onClick={() => setEditingPayment(null)} variant="outline" className="flex-1"><X size={16} className="mr-2" /> Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PendingPaymentsDetailView;
